import { computed } from 'vue'
import { EventsService, EventNameEnum } from '@/types/generated'
import { useUserStore } from '@/shared/stores/userStore'

// Export the auto-generated event types for convenience
export { EventNameEnum as MetricEventType }

export interface EventProperties {
  [key: string]: any
}

export interface MetricEvent {
  event_name: EventNameEnum
  properties?: EventProperties
}

export function useMetrics() {
  const userStore = useUserStore()
  const user = computed(() => userStore.user)

  /**
   * Track any generic event with optional properties
   */
  const trackEvent = async (eventName: EventNameEnum, properties: EventProperties = {}) => {
    try {
      // Build context properties, excluding empty values
      const contextProperties: EventProperties = {
        timestamp: new Date().toISOString()
      }

      // Only include user_id if user exists and has uuid
      if (user.value?.uuid) {
        contextProperties.user_id = user.value.uuid
      }

      // Only include law_firm_id if user has org with uuid
      if (user.value?.org?.uuid) {
        contextProperties.law_firm_id = user.value.org.uuid
      }

      // Filter out undefined/null/empty string values from user properties
      const cleanProperties = Object.entries(properties).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = value
        }
        return acc
      }, {} as EventProperties)

      const eventProperties = {
        ...contextProperties,
        ...cleanProperties // User-provided properties override defaults
      }

      await EventsService.eventsTrackCreate({
        requestBody: {
          event_name: eventName,
          properties: eventProperties
        }
      })
    } catch (error) {
      console.error(`Failed to track event "${eventName}":`, error)
    }
  }

  /**
   * Track multiple events in batch (useful for complex user flows)
   */
  const trackEvents = async (events: MetricEvent[]) => {
    const promises = events.map((event) => trackEvent(event.event_name, event.properties))

    const results = await Promise.allSettled(promises)
    const failures = results.filter((r) => r.status === 'rejected')
    if (failures.length > 0) {
      console.error(`Failed to track ${failures.length} of ${events.length} events`)
    }
  }

  return {
    trackEvent,
    trackEvents
  }
}
